import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  AccountProfile,
  clearToken,
  getProfile,
  getStoredToken,
  loginWithPassword,
  registerWithCode,
  resetPasswordWithCode,
} from '../lib/api';

type AuthContextValue = {
  user: AccountProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, code: string, password: string) => Promise<void>;
  resetPassword: (email: string, code: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AccountProfile | null>(null);
  const [loading, setLoading] = useState(Boolean(getStoredToken()));

  useEffect(() => {
    if (!getStoredToken()) return;
    let active = true;
    getProfile()
      .then((profile) => {
        if (active) setUser(profile);
      })
      .catch(() => {
        clearToken();
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    login: async (email, password) => {
      const profile = await loginWithPassword(email, password);
      setUser(profile);
    },
    register: async (email, code, password) => {
      const profile = await registerWithCode({ email, code, password });
      setUser(profile);
    },
    resetPassword: async (email, code, password) => {
      const profile = await resetPasswordWithCode({ email, code, newPassword: password });
      setUser(profile);
    },
    logout: () => {
      clearToken();
      setUser(null);
    },
  }), [loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}
